import { Component, Input, OnChanges, output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { NgbDropdownModule, NgbHighlight, NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbdSortableHeader, SortEvent } from './sortable.directive';

// FontAwesome
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowsRotate, faDownload, faMinus, faPlus, faRightFromBracket, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faUser } from '@fortawesome/free-regular-svg-icons';

// Models
import { ActionType, MultiAction } from '../../models/multi-action.model';
import { Column } from '../../models/column.model';

// Components
import { TableSkeletonLoaderComponent } from '../table-skeleton-loader/table-skeleton-loader.component';
import { FileIconComponent } from "../file-icon/file-icon.component";
import { BadgeStatusComponent } from "../badge-status/badge-status.component";

// Pipes
import { ByteConverterPipe } from "../../pipes/byte-converter.pipe";


@Component({
	selector: 'app-shared-table',
	imports: [
		DecimalPipe,
		DatePipe,
		FormsModule,
		NgbHighlight,
		NgbdSortableHeader,
		NgbPaginationModule,
		FontAwesomeModule,
		TableSkeletonLoaderComponent,
		FileIconComponent,
		ByteConverterPipe,
		BadgeStatusComponent,
		NgbDropdownModule
	],
	templateUrl: './table.component.html',
	styleUrl: './table.component.scss',
	providers: [DecimalPipe]
})
export class TableComponent implements OnChanges {
	faTrash = faTrash;
	faDownload = faDownload;
	faRightFromBracket = faRightFromBracket;
	faPlus = faPlus;
	faMinus = faMinus;
	faArrowsRotate = faArrowsRotate;
	faUser = faUser;

	selectedRows: any[] = [];
	currentSort: SortEvent = { column: '', direction: '' };

	@Input() items: any[] = [];
	@Input() columns!: Column[];
	@Input() isLoading = false;
	@Input() multiActions!: MultiAction[]
	@Input() clickableRow: boolean = true;
	@Input() tableHead: 'none' | 'trasparent' | 'solid' = 'trasparent';
	@Input() searchTerm: string = '';
	@Input() tableHeightElems: number = 10;
	@Input() emptyStateTitle: string = $localize`Nessun elemento trovato`;

	// Pagination
	@Input() totalItems: number = 0;
	@Input() pageSize: number = 5;
	@Input() currentPage: number = 1;
	pageChange = output<any>();
	sortChange = output<any>();

	openDetailEvent = output<any>();
	navigateSubListElem = output<{ item: any, colKey: string }>();
	singleActionEvent = output<{ item: any, action: 'verify' | 'reload' | 'delete' | 'unlink' | 'download' }>();
	multiActionEvent = output<{ list: any[], action: ActionType }>();

	@ViewChildren(NgbdSortableHeader) headers!: QueryList<NgbdSortableHeader>;

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['isLoading'] && changes['isLoading'].currentValue === true) {
			this.selectedRows = [];
		}
	}

	trackByFn(index: number, col: any): string {
		return col.key; // Use unique column identifiers
	}

	onSort({ column, direction }: any) {
		this.currentSort = { column, direction };
		// resetting other headers
		this.headers.forEach((header) => {
			if (header.sortable !== column) {
				header.direction = '';
			}
		});

		this.sortChange.emit({ column: direction ? column : '', direction });
	}

	onPageChange(page: number) {
		this.pageChange.emit(page); // Comunica la nuova pagina al parent
	}

	getStartIndex(): number {
		return (this.currentPage - 1) * this.pageSize + 1;
	}

	getEndIndex(): number {
		return Math.min(this.currentPage * this.pageSize, this.totalItems);
	}

	selectRow(item: any) {
		if (this.selectedRows.find(el => el[this.columns[0].key] === item[this.columns[0].key])) {
			this.selectedRows = this.selectedRows.filter(el => el[this.columns[0].key] !== item[this.columns[0].key]);
		} else {
			this.selectedRows.push(item);
		}
	}

	isSelected(itemId: string) {
		return this.selectedRows.find(el => el[this.columns[0].key] === itemId) ? true : false;
	}

	openDetail(event: Event, item: any) {
		event.stopPropagation();
		this.openDetailEvent.emit(item);
	}

	subListElemClick(event: Event, item: any, colKey: string) {
		event.stopPropagation();
		this.navigateSubListElem.emit({ item, colKey });
	}
}
